import React from "react";
import { cn } from "@/lib/utils";

/**
 * PartnerStrip — horizontal, infinite marquee of partner / integration brand logos.
 * Uses simple-icons CDN for COLORED brand SVGs. Includes graceful text fallback.
 * Edge fade-out gradients on both sides. Pauses on hover.
 */
const PARTNERS = [
    { name: "Twilio", slug: "twilio", color: "F22F46" },
    { name: "Zapier", slug: "zapier", color: "FF4F00" },
    { name: "Stripe", slug: "stripe", color: "635BFF" },
    { name: "Google Ads", slug: "googleads", color: "4285F4" },
    { name: "Search Console", slug: "googlesearchconsole", color: "458CF5" },
    { name: "Canva", slug: "canva", color: "00C4CC" },
    { name: "Ahrefs", slug: "ahrefs", color: "0067D9" },
    { name: "Google Analytics", slug: "googleanalytics", color: "E37400" },
    { name: "Mailgun", slug: "mailgun", color: "F06B66" },
    { name: "Semrush", slug: "semrush", color: "FF642D" },
    { name: "Meta", slug: "meta", color: "0467DF" },
    { name: "GoDaddy", slug: "godaddy", color: "1BDBDB" },
    { name: "OpenAI", slug: "openai", color: "412991" },
    { name: "Google Business", slug: "googlemybusiness", color: "4285F4" },
    { name: "LocalFalcon", slug: null, color: "188bf6" }, // not on simple-icons — text fallback
];

function PartnerLogo({ p }) {
    if (!p.slug) {
        return (
            <span
                className="font-display font-extrabold text-base sm:text-lg tracking-tight whitespace-nowrap"
                style={{ color: `#${p.color}` }}
            >
                {p.name}
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-2.5">
            <img
                src={`https://cdn.simpleicons.org/${p.slug}/${p.color}`}
                alt={p.name}
                className="h-6 sm:h-7 w-auto select-none"
                draggable="false"
                loading="lazy"
                onError={(e) => {
                    // graceful text fallback if icon CDN fails
                    e.currentTarget.style.display = "none";
                }}
            />
            <span
                className="font-display font-bold text-[13px] sm:text-sm whitespace-nowrap"
                style={{ color: `#${p.color}` }}
            >
                {p.name}
            </span>
        </span>
    );
}

export function PartnerStrip({ className = "", tone = "dark", testId = "partner-strip" }) {
    // Doubled for seamless infinite marquee
    const items = [...PARTNERS, ...PARTNERS];
    const isDark = tone === "dark";

    return (
        <section
            data-testid={testId}
            className={cn(
                "relative overflow-hidden py-10 sm:py-12",
                isDark ? "bg-[#0a0b0d]" : "bg-white",
                className,
            )}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-7 sm:mb-9">
                    <div
                        className={cn(
                            "inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-display font-bold",
                            isDark ? "text-white/65" : "text-[#4a5562]",
                        )}
                    >
                        <span className="inline-block h-[2px] w-7 bg-[#188bf6]" aria-hidden="true" />
                        Partners & Integrations
                    </div>
                    <h2
                        className={cn(
                            "mt-3 font-display font-extrabold text-2xl sm:text-3xl lg:text-4xl tracking-tight",
                            isDark ? "text-white" : "text-[#0a0b0d]",
                        )}
                    >
                        The stack runs on the tools{" "}
                        <span className="text-[#188bf6]">you already trust.</span>
                    </h2>
                </div>
            </div>

            {/* Marquee track */}
            <div className="relative">
                {/* Fade-out gradients on edges */}
                <div
                    aria-hidden="true"
                    className="absolute inset-y-0 left-0 w-20 sm:w-36 z-10 pointer-events-none"
                    style={{
                        background: isDark
                            ? "linear-gradient(to right, #0a0b0d 0%, rgba(10,11,13,0) 100%)"
                            : "linear-gradient(to right, #ffffff 0%, rgba(255,255,255,0) 100%)",
                    }}
                />
                <div
                    aria-hidden="true"
                    className="absolute inset-y-0 right-0 w-20 sm:w-36 z-10 pointer-events-none"
                    style={{
                        background: isDark
                            ? "linear-gradient(to left, #0a0b0d 0%, rgba(10,11,13,0) 100%)"
                            : "linear-gradient(to left, #ffffff 0%, rgba(255,255,255,0) 100%)",
                    }}
                />
                <div
                    data-testid="partner-marquee"
                    className="flex animate-marquee-x hover:[animation-play-state:paused] gap-10 sm:gap-14 w-max"
                >
                    {items.map((p, i) => (
                        <div
                            key={`${p.name}-${i}`}
                            className={cn(
                                "shrink-0 inline-flex items-center px-4 py-2 rounded-xl transition-transform duration-200 hover:scale-105",
                                isDark
                                    ? "bg-white/[0.04] border border-white/12"
                                    : "bg-[#F7F8FA] border border-[#0a0b0d]/10",
                            )}
                        >
                            <PartnerLogo p={p} />
                        </div>
                    ))}
                </div>
            </div>

            <p
                className={cn(
                    "mt-7 sm:mt-9 text-center text-[12px] sm:text-sm px-6",
                    isDark ? "text-white/55" : "text-[#4a5562]",
                )}
            >
                We don't reinvent the wheel — we wire the wheel into your
                business so it actually spins.
            </p>
        </section>
    );
}

export default PartnerStrip;
